import React, { useRef, useEffect, useCallback } from 'react';
import FormGroup from './FormGroup';
import './Textarea.css';

const Textarea = ({
  label,
  helpText,
  error,
  required,
  optional,
  block,
  rows = 1,
  className = '',
  value,
  onChange,
  ...props
}) => {
  const ref = useRef(null);

  // Grow with the content, but never shrink below the `rows` the caller asked
  // for. scrollHeight measures CONTENT, and a placeholder is not content — so
  // an empty field collapsed to a single line, which hid a multi-line
  // placeholder that exists precisely to be read BEFORE anything is typed. The
  // agent guide is nine lines of template you are meant to fill in; it was
  // rendering as an empty sliver.
  //
  // It only became visible here because a folio block strips the control's
  // padding and border, so the collapsed field had nothing left to see. The
  // arithmetic was always wrong.
  const autoResize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const styles = getComputedStyle(el);
    const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5 || 20;
    const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom) || 0;
    const floor = lineHeight * rows + padding;
    el.style.height = Math.max(el.scrollHeight, floor) + 'px';
  }, [rows]);

  useEffect(() => { autoResize(); }, [value, autoResize]);

  return (
    <FormGroup label={label} helpText={helpText} error={error} required={required} optional={optional} block={block}>
      <textarea
        ref={ref}
        className={`p-textarea p-textarea--auto ${className}`}
        rows={rows}
        required={required}
        value={value}
        onChange={onChange}
        {...props}
      />
    </FormGroup>
  );
};

export default Textarea;
